import {X} from 'lucide-react'
import {TooltipProvider} from '@/components/ui/tooltip'
import {Button} from '@/components/ui/button'
import {Card, CardContent, CardHeader, CardTitle} from '@/components/ui/card'
import {useI18n} from '@/i18n'
import {useFormLayoutPalette} from '@/hooks/useFormLayoutPalette'
import {FieldProperties} from './FieldProperties'
import {SectionProperties} from './SectionProperties'
import type {DesignerAction, DesignerSelection, LayoutSchema} from '@/types/form-layout'

interface PropertyPanelProps {
    schema: LayoutSchema
    selection: DesignerSelection
    dispatch: React.Dispatch<DesignerAction>
    useCaseKey: string
    onClose: () => void
}

export function PropertyPanel({schema, selection, dispatch, useCaseKey, onClose}: PropertyPanelProps) {
    const i18n = useI18n()
    const {palette} = useFormLayoutPalette(useCaseKey)

    if (!selection) return null

    return (
        <TooltipProvider>
            <div className="mt-4">
                <Card className="mx-auto max-w-lg">
                    <CardHeader className="flex flex-row items-center justify-between py-3 px-4">
                        <CardTitle className="text-sm" data-testid="card-title-properties">
                            {selection.type === 'field'
                                ? i18n.formLayouts.fieldProperties
                                : i18n.formLayouts.sectionProperties}
                        </CardTitle>
                        <Button
                            data-testid="property-panel-close-btn"
                            variant="ghost"
                            size="sm"
                            className="h-6 w-6 p-0"
                            onClick={onClose}
                        >
                            <X className="h-4 w-4" />
                        </Button>
                    </CardHeader>
                    <CardContent className="px-4 pb-4 pt-0">
                        {selection.type === 'field' && (
                            <FieldProperties
                                schema={schema}
                                sectionKey={selection.sectionKey}
                                fieldIndex={selection.fieldIndex}
                                dispatch={dispatch}
                                palette={palette}
                            />
                        )}

                        {selection.type === 'section' && (
                            <SectionProperties
                                schema={schema}
                                sectionKey={selection.sectionKey}
                                dispatch={dispatch}
                            />
                        )}
                    </CardContent>
                </Card>
            </div>
        </TooltipProvider>
    )
}
